// Colour preview swatches share the selected colour with the product order grid.

const COLOUR_PREVIEW_SWATCH_MAP = {
  black: '#111111',
  charcoal: '#36454F',
  grey: '#9B9B9B',
  gray: '#9B9B9B',
  navy: '#1C2951',
  white: '#FFFFFF',
  red: '#CC0000',
  blue: '#1B4B8A',
  bluecoat: '#315E85',
  green: '#2A6B45',
  bango: '#9F7AEA',
  emerald: '#0F9D72',
  jade: '#00B5B0',
  brown: '#6B3A2A',
  bottle: '#0F4D3A',
  'bottle green': '#0F4D3A',
  'dark bottle': '#082F24',
  maroon: '#800000',
  mulbery: '#70193D',
  mulberry: '#70193D',
  royal: '#234AA8',
  sky: '#87BFE8',
  silver: '#C0C0C0',
  amber: '#F6B23A',
  amethyst: '#8E6BBE',
  'icelandic amethyst': '#7450B4',
  toffee: '#8B5A2B',
  'toffee brown': '#8B5A2B',
  lavender: '#BBA7E8',
  lilac: '#C8A2C8',
  mint: '#98D8C8',
  turquoise: '#30C5C8',
  gold: '#D4AF37',
  ochre: '#CC8A00',
  coral: '#F06F61',
  'dark coral': '#C94D4D',
  'candyfloss coral': '#F07F7A',
  'cornflower blue': '#6495ED',
  'baby blue': '#A7D8F2',
  'baby pink': '#F4B6C8',
  'slate grey': '#292B2E',
  'leopard print': '#C9A07D',
  'rainbow leopard': '#C9A07D',
  'burnt orange': '#C65A1E',
  'buttermilk yellow': '#F6E59A',
  'fudge brown': '#7A4A2B',
  'geothermal grey': '#7D858C',
  'ice blue': '#B7DDF2',
  'pampas cream': '#E8DDC6',
  pistachio: '#A5C78A',
  peach: '#F7B28A',
  'sketchy sun print': '#F36C21',
  'sketchy sun': '#F36C21',
  'fanta orange': '#F36C21',
  beige: '#D2B48C',
  unbleached: '#E3DFD6',
  cream: '#FFFDD0',
  khaki: '#7C8B5A',
  burgundy: '#800020',
  pink: '#FFC0CB',
  rose: '#B76E79',
  'rose pink': '#D8A0A6',
  purple: '#6B3FA0',
  yellow: '#F5C400',
  orange: '#E8883A',
  camel: '#C19A6B',
  slate: '#708090',
  stone: '#e0d9c8ff',
  teal: '#008080',
  sand: '#D4B483',
  'glacial green': '#A8D5A1',
  glacialgreen: '#A8D5A1',
  'khaki green': '#7C8B5A',
  khakigreen: '#7C8B5A',
  olive: '#A8A96B',
  'olive green': '#A8A96B',
  olivegreen: '#A8A96B',
  'purple thistle': '#C6B1D4',
  purplethistle: '#C6B1D4',
  'peppa pink': '#F4A3B5',
  peppapink: '#F4A3B5',
  'pepper blue': '#75BCE6',
  pepperblue: '#75BCE6',
  'powder blue': '#A8CBE3',
  powderblue: '#A8CBE3',
  'orchid pink': '#E84D9A',
  orchidpink: '#E84D9A',
  'orchard pink': '#E84D9A',
  orchardpink: '#E84D9A',
};

const COLOUR_PREVIEW_SWATCH_ALIASES = {
  whteme: 'white / emerald',
};

function normaliseColourName(name) {
  return String(name || '')
    .toLowerCase()
    .replace(/^(?:[a-z]*\d+[a-z0-9]*|\d+[a-z0-9]*)[\s_-]+/i, '')
    .replace(/\b(mto|ltd|so|pu|sebs|leather|patent)\b/gi, '')
    .replace(/[()]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function getColourSwatchBackground(name) {
  const cleanName = normaliseColourName(name);
  const expandedName = COLOUR_PREVIEW_SWATCH_ALIASES[cleanName] || cleanName;
  const parts = expandedName
    .split(/\s*\/\s*|\s+\+\s+|\s+and\s+/i)
    .map((part) => part.trim())
    .filter(Boolean);

  const colours = parts
    .map((part) => {
      if (COLOUR_PREVIEW_SWATCH_MAP[part]) return COLOUR_PREVIEW_SWATCH_MAP[part];

      const matchedName = Object.keys(COLOUR_PREVIEW_SWATCH_MAP)
        .filter((colourName) => part.startsWith(`${colourName} `))
        .sort((first, second) => second.length - first.length)[0];
      if (matchedName) return COLOUR_PREVIEW_SWATCH_MAP[matchedName];

      const words = part.split(/\s+/).reverse();
      const matchedWord = words.find((word) => COLOUR_PREVIEW_SWATCH_MAP[word]);
      return matchedWord ? COLOUR_PREVIEW_SWATCH_MAP[matchedWord] : null;
    })
    .filter(Boolean);

  if (colours.length === 1) return colours[0];
  if (colours.length === 2) return `linear-gradient(135deg, ${colours[0]} 0 50%, ${colours[1]} 50% 100%)`;
  if (colours.length === 3) return `linear-gradient(135deg, ${colours[0]} 0 33%, ${colours[1]} 33% 66%, ${colours[2]} 66% 100%)`;

  return '#888888';
}

function isNeutralSwatch(value) {
  const cleanValue = String(value || '').trim().toLowerCase();
  if (!cleanValue) return true;
  if (cleanValue === '#888888' || cleanValue === 'rgb(136, 136, 136)') return true;
  const isMidtoneGrey = (channels) => channels[0] === channels[1]
    && channels[1] === channels[2]
    && channels[0] > 16
    && channels[0] < 240;

  const hexMatch = cleanValue.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (hexMatch) {
    const hex = hexMatch[1].length === 3
      ? hexMatch[1].split('').map((channel) => channel + channel)
      : hexMatch[1].match(/../g);
    return isMidtoneGrey(hex.map((channel) => parseInt(channel, 16)));
  }

  const rgbMatch = cleanValue.match(/^rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)/i);
  return Boolean(rgbMatch && isMidtoneGrey(rgbMatch.slice(1, 4).map(Number)));
}

function hasNamedColourCorrection(name) {
  const cleanName = normaliseColourName(name);
  return [
    'dark bottle',
    'white',
    'icelandic amethyst',
    'glacial green',
    'glacialgreen',
    'khaki',
    'khaki green',
    'khakigreen',
    'olive',
    'olive green',
    'olivegreen',
    'purple thistle',
    'purplethistle',
    'peppa pink',
    'peppapink',
    'pepper blue',
    'pepperblue',
    'powder blue',
    'powderblue',
    'orchid pink',
    'orchidpink',
    'orchard pink',
    'orchardpink',
  ].includes(cleanName) || cleanName.startsWith('dark bottle ');
}

function getMediaAlt(mediaItem) {
  const image = mediaItem?.querySelector('img');
  const alt = image?.getAttribute('alt')?.trim();
  if (alt) return alt;

  // Some Shopify uploads have no alt text even though the filename carries
  // the colour and view, e.g. 855BlackBack.jpg. Use that filename so the
  // front/back pairing still works without changing the stored media.
  const source = image?.getAttribute('src') || image?.getAttribute('data-src') || '';
  if (!source) return '';

  try {
    const url = new URL(source, document.baseURI);
    return decodeURIComponent(url.pathname.split('/').pop() || '');
  } catch (error) {
    return source.split(/[?#]/)[0].split('/').pop() || '';
  }
}

function getMediaGroup(alt) {
  return String(alt || '')
    .toLowerCase()
    .replace(/\.(?:avif|gif|jpe?g|png|webp)$/i, '')
    .replace(/[\s_-]*(?:front|back|primary|alternate)(?:[\s_-]+view)?(?:[\s_-]*\d+)?$/i, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function getMediaView(alt) {
  const cleanAlt = String(alt || '')
    .toLowerCase()
    .replace(/\.(?:avif|gif|jpe?g|png|webp)$/i, '')
    .trim();

  if (/(?:back|alternate)(?:[\s_-]+view)?(?:[\s_-]*\d+)?$/i.test(cleanAlt)) return 'back';
  if (/(?:front|primary)(?:[\s_-]+view)?(?:[\s_-]*\d+)?$/i.test(cleanAlt)) return 'front';
  return 'other';
}

if (!customElements.get('product-colour-preview')) {
  customElements.define(
    'product-colour-preview',
    class ProductColourPreview extends HTMLElement {
      connectedCallback() {
        this.valueLabel = this.querySelector('[data-colour-preview-value]');
        this.swatchButtons = Array.from(this.querySelectorAll('.colour-preview__swatch'));
        this.onClick = this.onClick.bind(this);
        this.onGalleryClick = this.onGalleryClick.bind(this);
        this.onGallerySlideChanged = this.onGallerySlideChanged.bind(this);
        this.addEventListener('click', this.onClick);
        this.fillMissingSwatches();

        this.selectedButton = this.swatchButtons.find((button) => button.getAttribute('aria-pressed') === 'true');
        window.requestAnimationFrame(() => {
          if (!this.isConnected) return;
          this.bindGalleryControls();
          this.bindBulkOrder();

          if (this.selectedButton) {
            this.activeMediaId = this.selectedButton.dataset.mediaId;
            this.showMediaPair(this.selectedButton.dataset.mediaId, false);
          }
        });
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        this.bulkOrderObserver?.disconnect();
        this.bulkOrderElement = null;
        this.galleryElement?.removeEventListener('click', this.onGalleryClick, true);
        this.galleryElement?.removeEventListener('slideChanged', this.onGallerySlideChanged);
      }

      get gallery() {
        return document.getElementById(`MediaGallery-${this.dataset.section}`);
      }

      get showAllProductMedia() {
        return this.closest('product-info')?.dataset.productId === '12190048911624';
      }

      bindBulkOrder() {
        const productId = this.closest('product-info')?.dataset.productId;
        if (!productId) return;

        this.bulkOrderElement = Array.from(document.querySelectorAll('[data-product-bulk-order]')).find((element) => {
          try {
            const config = JSON.parse(element.dataset.pboConfig || element.dataset.product || '{}');
            return String(config.product?.id || config.id) === String(productId);
          } catch (error) {
            return false;
          }
        });
        if (!this.bulkOrderElement) return;

        this.pendingOrderColour = this.selectedButton?.dataset.colourName;
        this.bulkOrderObserver = new MutationObserver(() => this.syncBulkOrderColour());
        this.bulkOrderObserver.observe(this.bulkOrderElement, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['aria-checked', 'disabled'],
        });
        this.syncBulkOrderColour();
      }

      findOrderColourButton(colourName) {
        const name = String(colourName || '').trim().toLowerCase();
        return Array.from(this.bulkOrderElement?.querySelectorAll('.pbo__colour-btn') || []).find((button) => {
          const label = button.querySelector('.pbo__colour-name')?.textContent || button.textContent;
          return label.trim().toLowerCase() === name;
        });
      }

      activateOrderColour(button) {
        const stopPageScroll = (event) => event.stopPropagation();
        this.bulkOrderElement.addEventListener('click', stopPageScroll);
        try {
          button.click();
        } finally {
          this.bulkOrderElement.removeEventListener('click', stopPageScroll);
        }
      }

      syncBulkOrderColour() {
        if (!this.isConnected || !this.bulkOrderElement) return;

        if (this.pendingOrderColour) {
          const pendingButton = this.findOrderColourButton(this.pendingOrderColour);
          if (pendingButton?.disabled) return;
          if (!pendingButton && !this.bulkOrderElement.querySelector('.pbo__colour-btn')) return;
          this.pendingOrderColour = null;
          if (pendingButton && pendingButton.getAttribute('aria-checked') !== 'true') {
            this.activateOrderColour(pendingButton);
            return;
          }
        }

        const selected = this.bulkOrderElement.querySelector('.pbo__colour-btn[aria-checked="true"]');
        if (!selected) return;
        const colourName = (selected.querySelector('.pbo__colour-name')?.textContent || selected.textContent).trim().toLowerCase();
        const previewButton = this.swatchButtons.find((button) => button.dataset.colourName?.trim().toLowerCase() === colourName);
        if (previewButton && previewButton !== this.selectedButton) this.selectPreviewColour(previewButton);
      }

      selectPreviewColour(button) {
        this.setGalleryMedia(button.dataset.mediaId);
        this.selectedButton = button;
        this.activeMediaId = button.dataset.mediaId;
        this.swatchButtons.forEach((swatchButton) => {
          swatchButton.setAttribute('aria-pressed', swatchButton === button ? 'true' : 'false');
        });
        if (this.valueLabel) this.valueLabel.textContent = button.dataset.colourName ?? '';
      }

      bindGalleryControls() {
        const gallery = this.gallery;
        if (!gallery || this.galleryElement === gallery) return;

        this.galleryElement = gallery;
        gallery.addEventListener('click', this.onGalleryClick, true);
        gallery.addEventListener('slideChanged', this.onGallerySlideChanged);

        gallery.querySelectorAll('[data-gallery-arrow]').forEach((button) => {
          button.removeAttribute('name');
          button.removeAttribute('disabled');
        });
      }

      getGalleryPair() {
        const gallery = this.galleryElement || this.gallery;
        const viewer = gallery?.querySelector('[id^="GalleryViewer"]');
        if (!gallery || !viewer) return null;

        const mediaItems = Array.from(viewer.querySelectorAll(this.showAllProductMedia ? '.product__media-item[data-media-id]' : '[data-media-id]'));
        const activeMedia = mediaItems.find((item) => item.classList.contains('is-active'));
        if (!activeMedia) return null;

        const mediaGroup = getMediaGroup(getMediaAlt(activeMedia));
        const pairedMedia = this.showAllProductMedia
          ? mediaItems
          : mediaItems.filter((item) => getMediaGroup(getMediaAlt(item)) === mediaGroup);

        return {
          gallery,
          pairedMedia,
          currentIndex: pairedMedia.indexOf(activeMedia),
        };
      }

      updateGalleryControls() {
        const gallery = this.galleryElement || this.gallery;
        const controls = gallery?.querySelector('.product-gallery-viewer__controls');
        const previous = controls?.querySelector('[data-gallery-arrow="previous"]');
        const next = controls?.querySelector('[data-gallery-arrow="next"]');
        const counter = controls?.querySelector('.slider-counter');
        const pair = this.getGalleryPair();

        if (!previous || !next) return;

        if (!pair || pair.pairedMedia.length < 2) {
          previous.hidden = true;
          next.hidden = true;
          return;
        }

        previous.removeAttribute('disabled');
        next.removeAttribute('disabled');
        previous.hidden = pair.currentIndex <= 0;
        next.hidden = pair.currentIndex >= pair.pairedMedia.length - 1;
        previous.setAttribute('aria-disabled', String(previous.hidden));
        next.setAttribute('aria-disabled', String(next.hidden));

        if (counter) {
          counter.querySelector('.slider-counter--current').textContent = String(pair.currentIndex + 1);
          counter.querySelector('.slider-counter--total').textContent = String(pair.pairedMedia.length);
        }
      }

      onGalleryClick(event) {
        const arrow = event.target.closest('[data-gallery-arrow]');
        if (arrow && this.galleryElement?.contains(arrow)) {
          event.preventDefault();
          event.stopPropagation();
          this.cycleGalleryMedia(arrow.dataset.galleryArrow === 'previous' ? -1 : 1);
          return;
        }

        const thumbnail = event.target.closest('[data-target]');
        if (!thumbnail || !this.galleryElement?.contains(thumbnail)) return;

        window.requestAnimationFrame(() => this.updateGalleryControls());
      }

      onGallerySlideChanged() {
        window.requestAnimationFrame(() => this.updateGalleryControls());
      }

      cycleGalleryMedia(direction) {
        const pair = this.getGalleryPair();
        if (!pair || pair.pairedMedia.length < 2) return;

        const nextMedia = pair.pairedMedia[pair.currentIndex + direction];
        if (!nextMedia) return;

        this.showMediaPair(nextMedia.dataset.mediaId, false);

        const stickyHeader = document.querySelector('sticky-header');
        const headerOffset = (stickyHeader?.getBoundingClientRect().height || 0) * 0.9;
        pair.gallery.setActiveMedia?.(nextMedia.dataset.mediaId, false, headerOffset, false, true);
        window.requestAnimationFrame(() => this.updateGalleryControls());
      }

      fillMissingSwatches() {
    this.swatchButtons.forEach((button) => {
      const swatch = button.querySelector('.swatch');
      if (!swatch) return;

      const current = swatch.style.getPropertyValue('--swatch--background').trim();
      const fallback = getColourSwatchBackground(button.dataset.colourName);
      const isSolidColour = /^(?:rgb|rgba|hsl|hsla|#)/i.test(current);
      const isCompositeColour = fallback.startsWith('linear-gradient(');
      if (!hasNamedColourCorrection(button.dataset.colourName)
        && current
        && !isNeutralSwatch(current)
        && !(isCompositeColour && isSolidColour)) return;

      swatch.style.setProperty('--swatch--background', fallback);
    });
  }

      setGalleryMedia(mediaId) {
        if (!mediaId || mediaId === this.activeMediaId) return;

        this.activeMediaId = mediaId;
        this.showMediaPair(mediaId);
      }

      showMediaPair(mediaId, activate = true) {
        const gallery = this.gallery;
        const viewer = gallery?.querySelector('[id^="GalleryViewer"]');
        const thumbnails = gallery?.querySelector('[id^="GalleryThumbnails"]');
        if (!gallery || !viewer) return;

        const mediaItems = Array.from(viewer.querySelectorAll(this.showAllProductMedia ? '.product__media-item[data-media-id]' : '[data-media-id]'));
        const primaryMedia = mediaItems.find((item) => item.dataset.mediaId === mediaId);
        if (!primaryMedia) return;

        const primaryGroup = getMediaGroup(getMediaAlt(primaryMedia));
        const matchingMedia = primaryGroup
          ? mediaItems.filter((item) => getMediaGroup(getMediaAlt(item)) === primaryGroup)
          : [primaryMedia];
        const frontMedia = matchingMedia.find((item) => getMediaView(getMediaAlt(item)) === 'front') || primaryMedia;
        const backMedia = matchingMedia.find((item) => getMediaView(getMediaAlt(item)) === 'back');
        const pairedMedia = this.showAllProductMedia ? mediaItems : [frontMedia];

        if (!this.showAllProductMedia && backMedia && backMedia !== frontMedia) pairedMedia.push(backMedia);

        const pairedIds = new Set(pairedMedia.map((item) => item.dataset.mediaId));
        gallery.classList.add('colour-preview-paired-gallery');
        gallery.classList.toggle('colour-preview-pair-single', pairedIds.size < 2);

        thumbnails?.querySelectorAll('[data-target]').forEach((thumbnail) => {
          const isMatch = pairedIds.has(thumbnail.dataset.target);
          thumbnail.hidden = !isMatch;
          thumbnail.classList.toggle('colour-preview-media-match', isMatch);
        });

        if (activate) {
          const stickyHeader = document.querySelector('sticky-header');
          const headerOffset = (stickyHeader?.getBoundingClientRect().height || 0) * 0.9;
          gallery.setActiveMedia?.(frontMedia.dataset.mediaId, false, headerOffset, false, true);
        }

        window.requestAnimationFrame(() => this.updateGalleryControls());
      }

      onClick(event) {
        const button = event.target.closest('.colour-preview__swatch');
        if (!button || !this.contains(button)) return;

        event.preventDefault();
        event.stopPropagation();

        const orderButton = this.findOrderColourButton(button.dataset.colourName);
        if (orderButton?.disabled) return;

        this.selectPreviewColour(button);
        if (this.bulkOrderElement && !orderButton) this.pendingOrderColour = button.dataset.colourName;
        if (orderButton && orderButton.getAttribute('aria-checked') !== 'true') this.activateOrderColour(orderButton);
      }

    }
  );
}

if (!customElements.get('product-card-colour-swatches')) {
  customElements.define(
    'product-card-colour-swatches',
    class ProductCardColourSwatches extends HTMLElement {
      connectedCallback() {
        this.cardWrapper = this.closest('.product-card-wrapper');
        this.primaryImage = this.cardWrapper?.querySelector('[data-card-primary-image]');
        this.swatchButtons = Array.from(this.querySelectorAll('[data-card-colour-swatch]'));
        this.selectedButton = this.querySelector('[data-card-colour-swatch][aria-pressed="true"]');
        this.persistedButton = null;
        this.originalImage = this.primaryImage
          ? {
              src: this.primaryImage.getAttribute('src') || '',
              srcset: this.primaryImage.getAttribute('srcset') || '',
              alt: this.primaryImage.getAttribute('alt') || '',
            }
          : null;
        this.onImageError = this.onImageError.bind(this);
        this.onClick = this.onClick.bind(this);
        this.onPointerOver = this.onPointerOver.bind(this);
        this.onPointerOut = this.onPointerOut.bind(this);
        this.onFocusIn = this.onFocusIn.bind(this);
        this.onFocusOut = this.onFocusOut.bind(this);
        this.addEventListener('click', this.onClick);
        this.addEventListener('pointerover', this.onPointerOver);
        this.addEventListener('pointerout', this.onPointerOut);
        this.addEventListener('focusin', this.onFocusIn);
        this.addEventListener('focusout', this.onFocusOut);
        this.primaryImage?.addEventListener('error', this.onImageError);
        this.fillMissingSwatches();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        this.removeEventListener('pointerover', this.onPointerOver);
        this.removeEventListener('pointerout', this.onPointerOut);
        this.removeEventListener('focusin', this.onFocusIn);
        this.removeEventListener('focusout', this.onFocusOut);
        this.primaryImage?.removeEventListener('error', this.onImageError);
      }

      fillMissingSwatches() {
        this.swatchButtons.forEach((button) => {
          const swatch = button.querySelector('.swatch');
          if (!swatch) return;

          const current = swatch.style.getPropertyValue('--swatch--background').trim();
          const fallback = getColourSwatchBackground(button.dataset.colourName);
          const isSolidColour = /^(?:rgb|rgba|hsl|hsla|#)/i.test(current);
          const isCompositeColour = fallback.startsWith('linear-gradient(');
          if (!hasNamedColourCorrection(button.dataset.colourName)
            && current
            && !isNeutralSwatch(current)
            && !(isCompositeColour && isSolidColour)) return;

          swatch.style.setProperty('--swatch--background', fallback);
        });
      }

      preview(button) {
        const imageSrc = button?.dataset.imageSrc?.trim();
        if (!this.primaryImage || !imageSrc || this.activeImageSrc === imageSrc) return;

        this.primaryImage.src = imageSrc;
        if (button.dataset.imageSrcset) {
          this.primaryImage.srcset = button.dataset.imageSrcset;
        } else {
          this.primaryImage.removeAttribute('srcset');
        }
        if (button.dataset.imageAlt) this.primaryImage.alt = button.dataset.imageAlt;
        this.activeImageSrc = imageSrc;
        this.cardWrapper?.classList.add('card-swatch-preview-active');
      }

      onImageError() {
        if (!this.primaryImage || !this.originalImage || this.activeImageSrc === this.originalImage.src) return;

        this.persistedButton = null;
        this.activeImageSrc = this.originalImage.src;
        this.primaryImage.src = this.originalImage.src;
        this.primaryImage.srcset = this.originalImage.srcset;
        this.primaryImage.alt = this.originalImage.alt;
        this.cardWrapper?.classList.remove('card-swatch-preview-active');
      }

      restoreSelected() {
        if (this.persistedButton?.dataset.imageSrc) {
          this.preview(this.persistedButton);
          return;
        }

        if (!this.primaryImage || !this.originalImage) return;
        this.primaryImage.src = this.originalImage.src;
        this.primaryImage.srcset = this.originalImage.srcset;
        this.primaryImage.alt = this.originalImage.alt;
        this.activeImageSrc = this.originalImage.src;
        this.cardWrapper?.classList.remove('card-swatch-preview-active');
      }

      onClick(event) {
        const button = event.target.closest('[data-card-colour-swatch]');
        if (!button || !this.contains(button)) return;

        event.preventDefault();
        event.stopPropagation();
        this.selectedButton = button;
        this.persistedButton = button;
        this.swatchButtons.forEach((swatchButton) => {
          swatchButton.setAttribute('aria-pressed', swatchButton === button ? 'true' : 'false');
        });
        this.preview(button);

        const variantUrl = button.dataset.variantUrl?.trim();
        if (variantUrl) window.location.assign(variantUrl);
      }

      onPointerOver(event) {
        const button = event.target.closest('[data-card-colour-swatch]');
        if (!button || !this.contains(button)) return;
        this.preview(button);
      }

      onPointerOut(event) {
        if (this.contains(event.relatedTarget)) return;
        this.restoreSelected();
      }

      onFocusIn(event) {
        const button = event.target.closest('[data-card-colour-swatch]');
        if (!button || !this.contains(button)) return;
        this.preview(button);
      }

      onFocusOut(event) {
        if (this.contains(event.relatedTarget)) return;
        this.restoreSelected();
      }
    }
  );
}

